import React, { createContext, useContext, useEffect, useState } from "react";
import { useLeaderboardQuery } from "@/api/vercel";
import { format, parseISO } from "date-fns";
import useCountdown from "@/hooks/useCountdown";

// Create the context
const RaceCountdownContext = createContext(null);

/**
 * Provider component for race countdown data
 */
export function RaceCountdownProvider({ children }) {
  const today = format(new Date(), "dd-MM-yyyy");
  const [activeRaces, setActiveRaces] = useState({});
  const [nextRaceToJump, setNextRaceToJump] = useState(null);

  // Keep a cache of calculated countdowns to avoid hook rule violations
  const [raceCountdowns, setRaceCountdowns] = useState({});

  // Fetch race data
  const { data: { data: leaderboardData } = {}, isLoading } =
    useLeaderboardQuery({
      date: today,
      collection: "predictions",
      staleTime: 30000, // Consider data fresh for 30 seconds
      refetchInterval: 60000, // Refetch every minute
    });

  // Process race data to extract market start times
  useEffect(() => {
    if (!leaderboardData) return;

    const now = new Date();
    const processedRaces = {};
    let closestUpcomingRace = null;
    let smallestTimeDifference = Infinity;

    // Process all races
    Object.entries(leaderboardData).forEach(([key, race]) => {
      if (!race || !race.marketStartTime) return;

      const marketStartTime = parseISO(race.marketStartTime);
      const isCompleted = marketStartTime < now;
      const timeDifference = marketStartTime - now;

      // Track the closest upcoming race
      if (
        !isCompleted &&
        timeDifference < smallestTimeDifference &&
        timeDifference > 0
      ) {
        smallestTimeDifference = timeDifference;
        closestUpcomingRace = {
          id: key,
          track: race.track,
          raceNumber: race.race,
          marketStartTime: marketStartTime,
        };
      }

      // Store race data with countdown information
      processedRaces[key] = {
        id: key,
        track: race.track,
        raceNumber: race.race,
        marketStartTime: marketStartTime,
        isCompleted,
      };
    });

    setActiveRaces(processedRaces);
    setNextRaceToJump(closestUpcomingRace);
  }, [leaderboardData]);

  // Calculate countdown for next race to jump
  const nextRaceCountdown = useCountdown(nextRaceToJump?.marketStartTime, {
    includeSeconds: true,
  });

  // Provide race data and countdown information
  const value = {
    races: activeRaces,
    nextRaceToJump,
    nextRaceCountdown,
    isLoading,
  };

  return (
    <RaceCountdownContext.Provider value={value}>
      {children}
    </RaceCountdownContext.Provider>
  );
}

/**
 * Hook to access race countdown data
 */
export function useRaceCountdown() {
  const context = useContext(RaceCountdownContext);
  if (!context) {
    throw new Error(
      "useRaceCountdown must be used within a RaceCountdownProvider",
    );
  }
  return context;
}

/**
 * Component-specific hook for a single race's countdown
 */
export function useRaceTimeRemaining(raceId, options = {}) {
  const { races } = useRaceCountdown();
  // If no raceId is provided or race doesn't exist, we still need to call useCountdown
  // with a default value to maintain hook call consistency
  const race = raceId ? races[raceId] : null;
  const marketStartTime = race?.marketStartTime || null;

  // Always call useCountdown with either the real time or null
  return useCountdown(marketStartTime, options);
}
